import * as s from "../TableItemStyled";
import { MouseEvent, useCallback, useMemo, useState } from "react";
import {
  ReportInventoryCameraRow,
  ReportInventoryRecorderRow,
} from "api/interfaces/reportInterface.interface";
import Type from "components/atoms/text/labels/Type";
import { convertType } from "utils/functions";
import RecorderTitle from "components/blocks/recorder/RecorderTitle";
import {
  BasePagination,
  LEVEL_TYPE,
} from "api/interfaces/commonInterface.interface";
import { commonOrgColumn } from "pages/dashboard/dashboard/WidgetContainer";
import {
  postWidgetInventoryCamera,
  postWidgetInventoryRecorder,
} from "api/widgetAPI";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import { useAppSelector } from "redux/hooks";
import AccountSelects from "../AccountSelects";
import SearchInput from "components/atoms/input/search/SearchInput";
import { DashboardChartDetailTable } from "components/atoms/table/DashboardChartDetailTable";
import {
  changeSortOrderAntd,
  onSortAntd,
} from "components/atoms/table/AntdTable";
import { TableColumnsType } from "antd";
import { ColumnType, ExpandableConfig } from "antd/es/table/interface";
import { getAccountTimeFormat } from "utils/timeUtil";
import { Layout } from "react-grid-layout";
import TableItemHeader from "../TableItemHeader";
import { useQueryKey } from "utils/queryUtil";
import { useInfiniteQuery } from "react-query";
import {
  PostWidgetAlertListResponse,
  PostWidgetInventoryCameraResponse,
} from "api/interfaces/widgetInterface.interface";
import * as mediaQuery from "components/MediaQuery";
import {
  cameraInitialQueryInfo,
  recorderInitialQueryInfo,
  TableItemName,
} from "utils/WidgetUtil";
import Tabs from "components/atoms/tabs/Tabs";
import InventoryExpandList from "pages/reports/view/inventory/InventoryExpandList";
import useStompDebouncedRefetch, {
  StompDateSignalKey,
} from "hook/useStompDebouncedRefetch";

interface Props {
  layout: Layout;
  chartLayout: Layout[];
  onDeleteWidget: (key: TableItemName) => void;
  onLayoutChange: (layout: Layout[]) => void;
}

export const enum INVENTORY_MENU {
  RECORDER = "Recorders",
  CAMERA = "Cameras",
}

export default function Inventory(props: Props): JSX.Element {
  const tabs = [
    { label: "Recorders", value: INVENTORY_MENU.RECORDER },
    { label: "Cameras", value: INVENTORY_MENU.CAMERA },
  ];

  const [currentMenu, setCurrentMenu] = useState<INVENTORY_MENU>(
    INVENTORY_MENU.RECORDER
  );
  const [accountIds, setAccountIds] = useState<string[]>([]);
  const [keyword, setKeyword] = useState<string>("");

  const onChangeTabMenu = (value: INVENTORY_MENU) => {
    setCurrentMenu(value);
  };
  const onChangeAccount = (value: string[]) => {
    setAccountIds(value);
  };

  const onClickSearch = (keyword: string) => {
    if (currentMenu === INVENTORY_MENU.RECORDER) {
      setKeyword(keyword);
    }
    if (currentMenu === INVENTORY_MENU.CAMERA) {
      setKeyword(keyword);
    }
  };

  return (
    <>
      <TableItemHeader
        layout={props.layout}
        chartLayout={props.chartLayout}
        onDeleteWidget={props.onDeleteWidget}
        onLayoutChange={props.onLayoutChange}
      />
      <s.Contents className="drag-cancel-content">
        <s.ContentWrapper>
          <s.ContentHeader>
            <div>
              <Tabs
                tabs={tabs}
                currentTab={currentMenu as string}
                onChangeTab={(value) =>
                  onChangeTabMenu(value as INVENTORY_MENU)
                }
              />
            </div>
            <div>
              <AccountSelects onChange={onChangeAccount} value={accountIds} />

              <SearchInput onClickSearch={onClickSearch} />
            </div>
          </s.ContentHeader>
          <s.ContentInner>
            {currentMenu === INVENTORY_MENU.RECORDER && (
              <InventoryRecorderTable
                accountIds={accountIds}
                keyword={keyword}
              />
            )}
            {currentMenu === INVENTORY_MENU.CAMERA && (
              <InventoryCameraTable accountIds={accountIds} keyword={keyword} />
            )}
          </s.ContentInner>
        </s.ContentWrapper>
      </s.Contents>
    </>
  );
}

function InventoryRecorderTable({
  accountIds,
  keyword,
}: {
  accountIds: string[];
  keyword: string;
}) {
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const [recorderQueryInfo, setRecorderQueryInfo] = useState<BasePagination>({
    ...recorderInitialQueryInfo,
  });
  const isMobile = mediaQuery.useIsMobile();
  const { pageNum, ...recorderQueryKey } = useQueryKey(recorderQueryInfo);
  const {
    data,
    isLoading: isInitialLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    isFetching,
  } = useInfiniteQuery(
    [
      "inventoryRecorder",
      recorderQueryKey,
      accountIds,
      selectedAccount.accountId,
      keyword,
    ],
    ({ pageParam = 0 }) =>
      postWidgetInventoryRecorder({
        accountId: selectedAccount.accountId,
        payload: {
          accountIds,
          listDto: {
            ...recorderQueryInfo,
            pageNum: pageNum === 0 ? 0 : pageParam,
            keyword,
          },
        },
      }),
    {
      getNextPageParam: (lastPage: PostWidgetAlertListResponse | undefined) => {
        if (!lastPage || !lastPage.page) {
          return undefined;
        }
        const { total, pageNum, pageLimit } = lastPage.page;
        const nextPage = pageNum + 1;
        return (pageNum + 1) * pageLimit < total ? nextPage : undefined;
      },
      refetchOnWindowFocus: false,
      retry: 0,
      onSuccess: (res: any) => {
        const last = res.pages[res.pages.length - 1];
        if (last) {
          setRecorderQueryInfo((info) => {
            return {
              ...info,
              ...last.page,
              pageNum: last.page.pageNum + 1,
            };
          });
        }
      },
    }
  );
  const lists = useMemo(() => {
    if (pageNum === 0 && (isInitialLoading || isFetching)) {
      return [];
    }
    return (data?.pages || []).flatMap(
      (page: PostWidgetAlertListResponse) => page.result ?? []
    ) as ReportInventoryRecorderRow[];
  }, [data, isFetching, isInitialLoading, pageNum]);
  const onHeaderCell = useCallback(
    (column: ColumnType<ReportInventoryRecorderRow>) => ({
      onClickCapture: (e: MouseEvent<HTMLElement>) => {
        e.preventDefault();
        e.stopPropagation();
        if (column.key) {
          setRecorderQueryInfo((info) => ({
            ...info,
            sortType: column.key as string,
            sortDirection: onSortAntd(info.sortDirection),
            pageNum: 0,
          }));
        }
      },
    }),
    []
  );

  const columns: TableColumnsType<ReportInventoryRecorderRow> = useMemo(
    () => [
      commonOrgColumn(selectedAccount),
      {
        title: "Type",
        key: "type",
        dataIndex: "type",
        width: isMobile ? 70 : 80,
        render: (value, row) => (
          <Type
            type={convertType(row)}
            licenseType={row.licenseType}
            isCloudCam={row.isCloudCam}
          />
        ),
      },
      {
        title: "Title",
        key: "mergedSystemName",
        dataIndex: "title",
        render: (value, row) => (
          <RecorderTitle
            mergedSystemName={row.mergedSystemName}
            recorderName={""}
            recorderCount={row.recorders?.length}
            type={row.type}
          />
        ),
        sorter: true,
        showSorterTooltip: false,
        onHeaderCell: onHeaderCell,
        sortOrder: changeSortOrderAntd(recorderQueryInfo, "mergedSystemName"),
        defaultSortOrder: "descend",
      },
      {
        title: "Cameras",
        key: "cameraCount",
        dataIndex: "cameraCount",
        align: "center",
        hidden: isMobile && selectedAccount.accountLevel === LEVEL_TYPE.CP,
        width: "10%",
      },
      {
        title: "Install Date",
        key: "createdAt",
        dataIndex: "installedDate",
        render: (value: string, row: ReportInventoryRecorderRow) => (
          <s.DateWrapper>
            {getAccountTimeFormat(
              row.installedDate ?? row.recorders[0].createdAt,
              selectedAccount,
              true
            )
              .split(" ")
              .map((date, index) => (
                <span key={index}>{date}</span>
              ))}
          </s.DateWrapper>
        ),
        hidden: isMobile,
        sorter: true,
        onHeaderCell: onHeaderCell,
        sortOrder: changeSortOrderAntd(recorderQueryInfo, "createdAt"),
        showSorterTooltip: false,
        defaultSortOrder: "descend",
        width: "30%",
      },
    ],
    [isMobile, onHeaderCell, recorderQueryInfo, selectedAccount]
  );
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);
  const expandableProps = useMemo<
    ExpandableConfig<ReportInventoryRecorderRow>
  >(() => {
    return {
      expandRowByClick: false,
      expandedRowKeys,
      onExpand: (expanded: boolean, record: ReportInventoryRecorderRow) => {
        if (expanded) {
          setExpandedRowKeys([record.mergedSystemId]);
        } else {
          setExpandedRowKeys([]);
        }
      },
      expandedRowRender: (record: ReportInventoryRecorderRow) => (
        <InventoryExpandList
          columns={columns as TableColumnsType<ReportInventoryRecorderRow>}
          data={record.recorders}
        />
      ),
      rowExpandable: (record: ReportInventoryRecorderRow) =>
        record.recorders?.length > 1,
    };
  }, [columns, expandedRowKeys]);
  return (
    <DashboardChartDetailTable
      rowKey="mergedSystemId"
      data={lists}
      loadMore={() => fetchNextPage()}
      isLoading={isInitialLoading || isFetchingNextPage || isFetching}
      hasMore={hasNextPage && !isFetchingNextPage && !isInitialLoading}
      columns={columns}
      expandable={expandableProps}
    />
  );
}

function InventoryCameraTable({
  accountIds,
  keyword,
}: {
  accountIds: string[];
  keyword: string;
}) {
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const isMobile = mediaQuery.useIsMobile();
  const [cameraQueryInfo, setCameraQueryInfo] = useState<BasePagination>({
    ...cameraInitialQueryInfo,
  });
  const { pageNum, ...cameraQueryKey } = useQueryKey(cameraQueryInfo);
  const {
    data,
    isLoading: isInitialLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch: refetchInventoryCamera,
    isFetching,
  } = useInfiniteQuery(
    [
      "inventoryCamera",
      cameraQueryKey,
      accountIds,
      selectedAccount.accountId,
      keyword,
    ],
    ({ pageParam = 0 }) =>
      postWidgetInventoryCamera({
        accountId: selectedAccount.accountId,
        payload: {
          accountIds,
          type: "",
          listDto: {
            ...cameraQueryInfo,
            pageNum: pageParam,
            keyword,
          },
        },
      }),
    {
      getNextPageParam: (
        lastPage: PostWidgetInventoryCameraResponse | undefined
      ) => {
        if (!lastPage || !lastPage.page) {
          return undefined;
        }
        const { total, pageNum, pageLimit } = lastPage.page;
        const nextPage = pageNum + 1;
        return (pageNum + 1) * pageLimit < total ? nextPage : undefined;
      },
      refetchOnWindowFocus: false,
      retry: 0,
      onSuccess: (res: any) => {
        const last = res.pages[res.pages.length - 1];
        if (last) {
          setCameraQueryInfo((info) => {
            return {
              ...info,
              ...last.page,
              pageNum: last.page.pageNum + 1,
            };
          });
        }
      },
    }
  );
  useStompDebouncedRefetch({
    stompKey: ["recorderListUpdated"] as StompDateSignalKey[],
    refetch: refetchInventoryCamera,
  });
  const lists = useMemo(() => {
    if (pageNum === 0 && (isInitialLoading || isFetching)) {
      return [];
    }
    return (data?.pages || []).flatMap(
      (page: PostWidgetInventoryCameraResponse) =>
        (page.result ?? []).map((item) => ({
          ...item,
          model: item.model || "N/A",
          manufacturer: item.manufacturer || "N/A",
        }))
    );
  }, [data, isFetching, isInitialLoading, pageNum]);
  const onHeaderCell = (column: ColumnType<ReportInventoryCameraRow>) => {
    return {
      onClickCapture: (e: MouseEvent<HTMLElement>) => {
        e.preventDefault();
        e.stopPropagation();
        if (column.key) {
          setCameraQueryInfo((info) => {
            return {
              ...info,
              sortType: column.key as string,
              sortDirection: onSortAntd(info.sortDirection),
              pageNum: 0,
            };
          });
        }
      },
    };
  };
  const columns: TableColumnsType<ReportInventoryCameraRow> = [
    commonOrgColumn(selectedAccount),
    {
      title: "Recorder",
      key: "mergedSystems.mergedSystemName",
      dataIndex: "recorderName",
      render: (value, row) => (
        <RecorderTitle
          mergedSystemName={row.mergedSystemName}
          recorderName={row.recorderName}
        />
      ),
      sorter: true,
      onHeaderCell: onHeaderCell,
      sortOrder: changeSortOrderAntd(
        cameraQueryInfo,
        "mergedSystems.mergedSystemName"
      ),
      showSorterTooltip: false,
      defaultSortOrder: "ascend",
    },
    {
      title: "Camera",
      key: "name",
      dataIndex: "name",
      sorter: true,
      onHeaderCell: onHeaderCell,
      sortOrder: changeSortOrderAntd(cameraQueryInfo, "name"),
      showSorterTooltip: false,
      defaultSortOrder: "ascend",
    },
    {
      title: "Manufacturer",
      key: "manufacturer",
      dataIndex: "manufacturer",
      hidden: isMobile,
    },
    {
      title: "Model",
      key: "model",
      dataIndex: "model",
      hidden: isMobile,
    },
    // {
    //   title: "MAC address",
    //   key: "macAddr",
    //   dataIndex: "macAddr",
    //   render: (value) => (value ? value : "N/A"),
    // },
  ];
  return (
    <DashboardChartDetailTable
      data={lists}
      loadMore={() => fetchNextPage()}
      isLoading={isInitialLoading || isFetchingNextPage || isFetching}
      hasMore={hasNextPage && !isFetchingNextPage && !isInitialLoading}
      columns={columns}
    />
  );
}
